import { ReactElement, useEffect, useState } from 'react'
import { AsyncThunk } from '@reduxjs/toolkit'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import PlusIcon from '@mui/icons-material/Add'

import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch } from 'src/store/hooks'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { AddIcon, CloseIcon, ConfirmIcon, DeleteIcon } from 'src/v3/core/icons'
import { Instructor } from '../../core/types'

interface Props {
  instructors: Instructor[]
  instructorIDs: string[]
  setInstructorIDs: React.Dispatch<React.SetStateAction<string[]>>
  apiPath: string
  linkedServiceID?: string
  fetchInstructors: AsyncThunk<Instructor[], void, object>
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
  submitted: boolean
  noneText?: string
}

interface FormFields {
  name: string
  credentials?: string
}

export const defaultValue = 'unselected'
const createValue = 'CREATE_NEW'

let SelectInstructors = ({
  instructors,
  instructorIDs,
  setInstructorIDs,
  apiPath,
  linkedServiceID,
  fetchInstructors,
  setErrorMessage,
  submitted,
  noneText,
}: Props): ReactElement => {
  let dispatch = useAppDispatch()

  let [open, setOpen] = useState(false)
  let [creating, setCreating] = useState(false)
  let [deleteInstructor, setDeleteInstructor] = useState<Instructor>()
  let [deleting, setDeleting] = useState<boolean>(false)

  useEffect(() => {
    if (
      instructorIDs.filter(
        (x) =>
          !instructors.some(
            (i) =>
              i.id === x &&
              i.linkedServiceID === (linkedServiceID || undefined),
          ),
      ).length > 0
    ) {
      setInstructorIDs([defaultValue])
    }
  }, [linkedServiceID])

  let setID = (id: string, index: number) =>
    setInstructorIDs(instructorIDs.map((x, i) => (i === index ? id : x)))

  let formValues: FormFields = {
    name: '',
    credentials: '',
  }

  let validationSchema = z.object({
    name: z.string().nonempty('Name is Required'),
    credentials: z.string(),
  })

  let form = useForm({
    defaultValues: formValues,
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      setCreating(true)
      axios
        .post<Instructor>(`/api/${apiPath}/instructor`, {
          name: value.name,
          linkedServiceID: linkedServiceID || undefined,
          credentials: value.credentials,
        })
        .then(pluckData)
        .then((instructor) =>
          dispatch(fetchInstructors()).then(() =>
            setID(
              instructor.id,
              instructorIDs.findIndex((id) => id === defaultValue),
            ),
          ),
        )
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Creating Instructor')
        })
        .finally(() => {
          setOpen(false)
          setCreating(false)
          form.reset()
        })
    },
  })

  return (
    <Box>
      <Box sx={{ mb: 0.5, mt: '-5px', display: 'flex', alignItems: 'center' }}>
        <Typography color="textSecondary">Instructors</Typography>
        <IconButton
          color="primary"
          size="small"
          disabled={instructorIDs.some((id) => id === defaultValue)}
          onClick={() => setInstructorIDs([...instructorIDs, defaultValue])}
        >
          <PlusIcon />
        </IconButton>
      </Box>
      {!instructorIDs.length && (
        <Typography>
          {noneText || 'You have not selected any instructors.'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {instructorIDs.map((id, i) => (
          <Box key={id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl
              key={id}
              fullWidth
              error={submitted && instructorIDs[i] === defaultValue}
            >
              <InputLabel>Instructor {i + 1}</InputLabel>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={instructorIDs[i]}
                label={`Instructor ${i + 1}`}
                name={`instructor-${i + 1}`}
                onChange={(e) => {
                  if (e.target.value === createValue) {
                    setOpen(true)
                  } else {
                    setID(e.target.value, i)
                  }
                }}
              >
                <MenuItem value={defaultValue}>Select Instructor</MenuItem>
                <MenuItem
                  value={createValue}
                  sx={{ color: 'primary.main', fontWeight: 'medium' }}
                >
                  <AddIcon sx={{ mr: 1, fontSize: 16 }} />
                  Create New Instructor
                </MenuItem>
                {instructors
                  .filter(
                    (instructor) =>
                      instructor.id === instructorIDs[i] ||
                      !instructorIDs.includes(instructor.id),
                  )
                  .map((instructor) => (
                    <MenuItem
                      key={instructor.id}
                      value={instructor.id}
                      sx={{ position: 'relative' }}
                    >
                      {instructor.name}
                      {instructorIDs[i] !== instructor.id && (
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: { xs: 6, sm: 0 },
                            zIndex: 1,
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setDeleteInstructor(instructor)
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <span>
              <IconButton
                onClick={() =>
                  setInstructorIDs(
                    instructorIDs.filter((x) => instructorIDs[i] !== x),
                  )
                }
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Box>
        ))}
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogTitle sx={{ mt: 1 }}>Create New Instructor</DialogTitle>
          <DialogContent sx={{ py: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <form.Field
                name="name"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="standard"
                    value={field.state.value}
                    label="Name"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                  />
                )}
              />
              <form.Field
                name="credentials"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="standard"
                    value={field.state.value}
                    label="Credentials"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" loading={creating}>
              Submit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        onClose={() => setDeleteInstructor(undefined)}
        open={!!deleteInstructor}
      >
        <Paper sx={{ py: 0.5, px: 2, maxWidth: 325 }}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            Confirm Instructor Deletion
          </DialogTitle>
          <DialogContent sx={{ px: 1 }}>
            <Typography>
              Are you sure you want to delete {deleteInstructor?.name}?
            </Typography>
            <Typography sx={{ mt: 1 }}>
              Doing so will remove this instructor from all courses and events
              that they teach or have taught.
            </Typography>
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3">
            <Button
              onClick={() => {
                setDeleting(true)
                axios
                  .delete(`/api/${apiPath}/instructor/${deleteInstructor?.id}`)
                  .then(() => dispatch(fetchInstructors()))
                  .catch((err) => {
                    setErrorMessage(err.message)
                  })
                  .finally(() => {
                    setDeleteInstructor(undefined)
                    setDeleting(false)
                  })
              }}
              color="primary"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              loading={deleting}
              startIcon={<ConfirmIcon />}
            >
              Confirm
            </Button>
            <Button
              onClick={() => setDeleteInstructor(undefined)}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
    </Box>
  )
}

export default SelectInstructors
